import type { EditorJsData } from './editorjs-types';

import { parseEditorJs } from './editorjs-types';

import { toaster } from '#src/components/ui/toaster';

/** Keep in sync with ask-ai-react-document.ts DEFAULT_EDIT_SUMMARY_MESSAGE */
export const FALLBACK_AI_EDIT_TOAST_DESCRIPTION =
  'Updates saved to the document.';

/**
 * Applies a stored ask-ai-react processor JSON result (the `ProcessorInstance.value` shape)
 * to the editor and shows success/error toasts.
 */
export async function applyAskAiReactStoredProcessorResult(
  stored: unknown,
  options: {
    onValueUpdate: (value: EditorJsData | null) => void;
    persistDocError: (
      message: string,
      instanceId?: string | null,
    ) => Promise<void>;
    instanceId?: string;
  },
): Promise<boolean> {
  const { onValueUpdate, persistDocError, instanceId } = options;
  if (stored == null) {
    await persistDocError('Processor returned no result', instanceId);
    return false;
  }
  const inner = stored as {
    success?: boolean;
    error?: string;
    value?: unknown;
    message?: unknown;
  };
  if (inner.success === false) {
    await persistDocError(inner.error ?? 'Request failed', instanceId);
    return false;
  }
  const value = typeof inner.value === 'string' ? inner.value : undefined;
  if (typeof value === 'string') {
    const parsed = parseEditorJs(value);
    if (!parsed) {
      await persistDocError(
        'Processor did not return valid Editor.js JSON',
        instanceId,
      );
      return false;
    }
    onValueUpdate(parsed);
    const summary =
      typeof inner.message === 'string' ? inner.message.trim() : '';
    toaster.create({
      title: 'Success',
      description: summary || FALLBACK_AI_EDIT_TOAST_DESCRIPTION,
    });
    return true;
  }
  await persistDocError('Processor did not return document value', instanceId);
  return false;
}
